import { useEffect, useMemo, useState } from "react";
import "./ReportFilesManager.css";
import {
  Button,
  Checkbox,
  CircleX,
  Input,
  NoticeBox,
  Plus,
  ProgressBar,
  Select,
  Tags,
  Upload,
} from "../../../ui";
import AppFileNode from "../AppFileNode/AppFileNode";
import DeleteSelectionToolbar from "../DeleteSelectionToolbar/DeleteSelectionToolbar";
import useSelectionDelete from "../../hooks/useSelectionDelete";
import UploadZone from "../UploadZone/UploadZone";
import ReportFileSlot, { fileNameOf, isVersionedReport } from "../ReportFileSlot/ReportFileSlot";
import { DeleteModal } from "../../../modals/Delete";
import { APPS, MONTH_NAMES } from "../../utils/ludDocuments.constants";
import { formatDate, getLudFileName, openLudFile } from "../../utils/ludDocuments.helpers";
import {
  createLudReportFileAPI,
  deleteLudReportFileAPI,
} from "../../../../utils/api/ludDocuments";

const now = new Date();
const CURRENT_YEAR = now.getFullYear();
const CURRENT_MONTH = now.getMonth() + 1;

const toMonthInputValue = (year, month) =>
  `${Number(year)}-${String(Number(month)).padStart(2, "0")}`;

const parseMonthInputValue = (value) => {
  const [year, month] = String(value || "").split("-").map(Number);

  return {
    year: Number.isFinite(year) ? year : "",
    month: Number.isFinite(month) ? month : "",
  };
};
const createForm = (enabledApps, defaults = {}) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  app: enabledApps[0] || "",
  year: defaults.year || CURRENT_YEAR,
  month: defaults.month || CURRENT_MONTH,
  period: defaults.period || "",
  files: [],
});

const buildCoverageDates = (year, month) => {
  const paddedMonth = String(month).padStart(2, "0");
  const lastDay = new Date(Number(year), Number(month), 0).getDate();
  const isCurrentMonth = Number(year) === CURRENT_YEAR && Number(month) === CURRENT_MONTH;

  return {
    coverageFrom: `${year}-${paddedMonth}-01`,
    coverageTo: isCurrentMonth
      ? new Date().toISOString().slice(0, 10)
      : `${year}-${paddedMonth}-${String(lastDay).padStart(2, "0")}`,
  };
};

export default function ReportFilesManager({
  restaurant,
  lang,
  token,
  refreshRestaurant,
  reportType,
  periodOptions,
  sharedDate = false,
  title,
  renderReports,
  canManage,
  onError,
}) {
  const ar = lang === "ar";
  const [sharedYear, setSharedYear] = useState(CURRENT_YEAR);
  const [sharedMonth, setSharedMonth] = useState(CURRENT_MONTH);
  const [forms, setForms] = useState(() => [createForm(restaurant.enabledApps)]);
  const [uploadingIds, setUploadingIds] = useState([]);
  // Per form: upload progress (0..1), a pending "files from before versioning" confirmation, the last outcome.
  const [formState, setFormState] = useState({});
  const patchFormState = (id, patch) => setFormState((current) => ({ ...current, [id]: { ...current[id], ...patch } }));
  const deletion = useSelectionDelete({
    deleteOne: (id) => deleteLudReportFileAPI(token, restaurant.id, id),
    refresh: refreshRestaurant,
    lang,
  });
  const { cancel: cancelDeletion } = deletion;

  const reports = useMemo(
    () => (restaurant.reportFiles || []).filter((report) => report.type === reportType),
    [restaurant.reportFiles, reportType],
  );

  const reportsByApp = useMemo(
    () => reports.reduce((result, report) => {
      result[report.app] = [...(result[report.app] || []), report];
      return result;
    }, {}),
    [reports],
  );

  // Reset only when the restaurant, its app list or the report type actually
  // change. Depending on the enabledApps array identity wiped every other
  // filled-in form (and its chosen files) after each upload's data refresh.
  const enabledAppsKey = restaurant.enabledApps.join("|");
  useEffect(() => {
    setForms([createForm(restaurant.enabledApps)]);
    cancelDeletion();
    setSharedYear(CURRENT_YEAR);
    setSharedMonth(CURRENT_MONTH);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant.id, enabledAppsKey, reportType]);

  const updateForm = (id, field, value) => {
    setForms((current) => current.map((form) => (
      form.id === id ? { ...form, [field]: value } : form
    )));
  };

  const addForm = () => {
    setForms((current) => [
      ...current,
      createForm(restaurant.enabledApps, {
        year: sharedDate ? sharedYear : CURRENT_YEAR,
        month: sharedDate ? sharedMonth : CURRENT_MONTH,
      }),
    ]);
  };

  const removeForm = (id) => {
    setForms((current) => current.length === 1
      ? current
      : current.filter((form) => form.id !== id));
  };

  // Every chosen file is added as a NEW part of the slot (app + type + month); replacing an existing
  // part is done on that file's row ("Replace this part").
  const uploadForm = async (form, { legacyAction } = {}) => {
    const year = sharedDate ? sharedYear : form.year;
    const month = sharedDate ? sharedMonth : form.month;
    if (!form.app || !year || !month || !form.period || !form.files.length || uploadingIds.includes(form.id)) return;

    setUploadingIds((current) => [...current, form.id]);
    patchFormState(form.id, { progress: 0, legacyPending: false, outcome: null });
    try {
      const coverage = reportType === "daily" ? buildCoverageDates(year, month) : {};
      const added = await createLudReportFileAPI(token, restaurant.id, {
        files: form.files,
        type: reportType,
        app: form.app,
        year,
        month,
        period: form.period,
        legacyAction,
        onProgress: (value) => patchFormState(form.id, { progress: value }),
        ...coverage,
      });
      updateForm(form.id, "files", []);
      patchFormState(form.id, {
        outcome: { variant: "success", message: ar ? `تمت إضافة ${added.length} جزء جديد.` : `${added.length} new part${added.length === 1 ? "" : "s"} added.` },
      });
      await refreshRestaurant();
    } catch (error) {
      if (error?.data?.code === "LEGACY_SLOT_UNRESOLVED") {
        patchFormState(form.id, { legacyPending: true });
      } else {
        if (Array.isArray(error?.data?.reports) && error.data.reports.length) await refreshRestaurant();
        onError?.(
          error,
          ar ? "تعذّر رفع الملفات" : "The files could not be uploaded",
        );
      }
    } finally {
      patchFormState(form.id, { progress: null });
      setUploadingIds((current) => current.filter((id) => id !== form.id));
    }
  };

  // Part / version / size details and the per-part actions of one report row.
  const renderFileSlot = (report) => (
    <ReportFileSlot
      report={report}
      siblings={reports}
      restaurant={restaurant}
      token={token}
      lang={lang}
      canManage={canManage}
      onReplaced={refreshRestaurant}
      onError={onError}
    />
  );

  return (
    <>
      {canManage && reports.length > 0 && (
        <DeleteSelectionToolbar selection={deletion} lang={lang} />
      )}

      {renderReports
        ? renderReports({
            reportsByApp,
            deleteMode: deletion.active,
            selectedIds: deletion.selected,
            toggleSelected: deletion.toggle,
            periodOptions,
            renderFileSlot,
          })
        : (
          restaurant.enabledApps.map((appKey) => {
        const appReports = (reportsByApp[appKey] || []).slice().sort((a, b) =>
          Number(b.year) - Number(a.year) || Number(b.month) - Number(a.month));

        return (
          <AppFileNode
            key={appKey}
            title={APPS[appKey].label[lang]}
            subtitle={ar ? `${appReports.length} ملف مضاف` : `${appReports.length} uploaded files`}
            status={appReports.length ? (ar ? "توجد ملفات" : "Files available") : (ar ? "لا توجد ملفات" : "No files")}
            statusVariant={appReports.length ? "success" : "neutral"}
          >
            {appReports.length ? appReports.map((report) => {
              const periodLabel = periodOptions.find((option) => option.value === report.period)?.label || report.period;
              return (
                <div className="lud-report-file lud-report-file--actual" key={report.id}>
                  {deletion.active && (
                    <Checkbox
                      checked={deletion.isSelected(report.id)}
                      onChange={() => deletion.toggle(report.id)}
                      onClick={(event) => event.stopPropagation()}
                    />
                  )}
                  <div className="lud-report-file__content">
                    <strong>{periodLabel} — {MONTH_NAMES[lang][Number(report.month) - 1]} {report.year}</strong>
                    <button
                      type="button"
                      className="lud-file-name-link"
                      onClick={() => openLudFile(report.currentFile, { token, storeId: restaurant.id, kind: "report", reference: report.id, onError })}
                    >
                      {isVersionedReport(report) ? fileNameOf(report) : report.currentFile ? getLudFileName({ file: { ...report.currentFile, app: appKey, type: reportType }, kind: "report", reference: report.id, originalName: report.currentFile.originalName }) : periodLabel}
                    </button>
                    {report.currentFile?.uploadedAt && (
                      <small>
                        {ar ? "رُفع بتاريخ" : "Uploaded on"} {formatDate(report.currentFile.uploadedAt, lang)}
                      </small>
                    )}
                    {renderFileSlot(report)}
                  </div>
                  <Tags variant="success">{ar ? "مضاف" : "Uploaded"}</Tags>
                </div>
              );
            }) : (
              <div className="lud-report-empty">{ar ? "لا توجد ملفات مضافَة لهذا التطبيق." : "No files have been added for this application."}</div>
            )}
          </AppFileNode>
        );
      })
        )}

      {canManage&&<section className="lud-report-upload-section">
        <div className="lud-report-upload-section__header">
          <div>
            <h3>{title}</h3>
            <p>{ar ? "أكمل بيانات الحقل ثم اختر الملفات واضغط إضافة. كل ملف يُضاف كجزء جديد؛ لاستبدال ملف موجود استخدم «استبدال هذا الجزء» في صفه." : "Complete the field details, choose the files, then click Add. Every file is added as a new part; to replace an existing file use “Replace this part” on its row."}</p>
          </div>
          <Button variant="primary" appearance="soft" icon={<Plus size={17} />} onClick={addForm}>
            {ar ? "إضافة حقل" : "Add field"}
          </Button>
        </div>

        {sharedDate && (
          <div className="lud-report-shared-date">
            <Input
              label={ar ? "الشهر والسنة الموحدان" : "Shared month and year"}
              type="month"
              value={toMonthInputValue(sharedYear, sharedMonth)}
              onChange={(event) => {
                const nextDate = parseMonthInputValue(event.target.value);
                setSharedYear(nextDate.year);
                setSharedMonth(nextDate.month);
              }}
            />
          </div>
        )}

        <div className="lud-report-upload-list">
          {forms.map((form, index) => {
            const fieldsReady = Boolean(
              form.app && form.period &&
              (sharedDate || (form.year && form.month)),
            );
            const uploading = uploadingIds.includes(form.id);
            const state = formState[form.id] || {};

            return (
              <div className="lud-report-upload-card" key={form.id}>
                <div className="lud-report-upload-card__title">
                  <strong>{ar ? `حقل ${index + 1}` : `Label ${index + 1}`}</strong>
                  {forms.length > 1 && (
                    <Button
                      variant="danger"
                      appearance="soft"
                      icon={<CircleX size={16} />}
                      onClick={() => removeForm(form.id)}
                    >
                      {ar ? "إزالة" : "Remove"}
                    </Button>
                  )}
                </div>

                <div className="lud-report-upload-card__fields">
                  <Select
                    label={ar ? "التطبيق" : "Application"}
                    value={form.app}
                    onChange={(event) => updateForm(form.id, "app", event.target.value)}
                    options={restaurant.enabledApps.map((key) => ({ value: key, label: APPS[key].label[lang] }))}
                  />
                  {!sharedDate && (
                    <Input
                      label={ar ? "الشهر والسنة" : "Month and year"}
                      type="month"
                      value={toMonthInputValue(form.year, form.month)}
                      onChange={(event) => {
                        const nextDate = parseMonthInputValue(event.target.value);
                        updateForm(form.id, "year", nextDate.year);
                        updateForm(form.id, "month", nextDate.month);
                      }}
                    />
                  )}
                  <Select
                    label={ar ? "نوع الملف" : "File type"}
                    value={form.period}
                    onChange={(event) => updateForm(form.id, "period", event.target.value)}
                    options={periodOptions}
                  />
                </div>

                <UploadZone
                  lang={lang}
                  label={form.files.length
                    ? ar
                      ? `${form.files.length} ملفات مختارة`
                      : `${form.files.length} files selected`
                    : ar
                      ? "اختيار الملفات"
                      : "Choose files"}
                  icon={<Upload size={20} />}
                  onFiles={(files) => updateForm(form.id, "files", files || [])}
                  disabled={!fieldsReady}
                  multiple
                  accept=".pdf,.xls,.xlsx,.xlsm,.xlsb,.csv"
                />

                <Button
                  className="lud-form-submit"
                  variant="primary"
                  appearance="filled"
                  icon={<Plus size={17} />}
                  onClick={() => uploadForm(form)}
                  disabled={!fieldsReady || !form.files.length || uploading}
                  loading={uploading}
                >
                  {ar ? "إضافة كأجزاء جديدة" : "Add as new parts"}
                </Button>
                {typeof state.progress === "number" && (
                  <ProgressBar value={state.progress * 100} label={ar ? "جارٍ الرفع" : "Uploading"} valueText={`${Math.round(state.progress * 100)}%`} />
                )}
                {state.legacyPending && (
                  <NoticeBox variant="warning" size="sm" title={ar ? "توجد ملفات مرفوعة قبل تفعيل الإصدارات" : "Files from before versioning exist"}>
                    <p>
                      {ar
                        ? "لهذا التطبيق والنوع والشهر ملفات رُفعت قبل تفعيل الإصدارات. لن تُستبدل ولن تُحذف. هل تريد إضافة الملفات المختارة كأجزاء جديدة بجانبها؟"
                        : "This application, type and month already has files uploaded before versioning. They will not be replaced or deleted. Add the chosen files as new parts beside them?"}
                    </p>
                    <Button size="sm" variant="warning" appearance="filled" onClick={() => uploadForm(form, { legacyAction: "add_part" })} disabled={uploading}>
                      {ar ? "إضافة كأجزاء جديدة" : "Add as new parts"}
                    </Button>
                  </NoticeBox>
                )}
                {state.outcome && (
                  <NoticeBox variant={state.outcome.variant} size="sm">
                    <span role="status">{state.outcome.message}</span>
                  </NoticeBox>
                )}
              </div>
            );
          })}
        </div>
      </section>}

      {deletion.confirmOpen && (
        <DeleteModal
          id={null}
          loading={deletion.deleting}
          error={deletion.error}
          handleDelete={deletion.confirm}
          cancel={deletion.closeConfirm}
        />
      )}
    </>
  );
}
